import { isScheduledStatus } from '@common/scheduling/status-scheduling';

/**
 * WHERE a send goes, as the planner draws it: the big circle on a sidebar row
 * and the faces on the calendar hover card. Pure, so both surfaces render one
 * decision and a later change cannot make them disagree.
 *
 * The product owner's ranking: destination first, kind second. So the circle
 * answers "to whom" — one person's avatar, an overlapping group for a campaign
 * or a multi-recipient send, the account's own Status for a Status — and the
 * kind moves to a small badge hung under the destination.
 *
 * ── Where the faces come from ──────────────────────────────────────────────
 * A campaign's audience is a board whose members are task rows. The campaign
 * document carries the audience ids and a `contactCount`, never the members.
 * This module never fetches: it plans from whatever ids it is handed. Those
 * come from rows already in the store (an audience another screen loaded) or,
 * for an audience nobody has opened, from ONE batched preview read shared by
 * every row on screen (audience-preview-loader.ts, via useCampaignDestination)
 * — never a request per row. Until a preview lands, or if it cannot be read,
 * the plan draws placeholder faces over the true count.
 */

/** Faces drawn before the rest collapse into "+N". */
export const MAX_FACES = 5;

export type DestinationPlan =
    /** The account's own WhatsApp Status. */
    | { mode: 'status' }
    /** One chat — a person or a WhatsApp group: its own avatar. */
    | { mode: 'single'; contactId: string }
    /**
     * An audience. `faces` holds at most MAX_FACES ids in send order; an empty
     * string is a member we cannot see (not loaded) and is drawn as the plain
     * person placeholder, which asks for no picture. `surplus` is everyone
     * else: faces.length + surplus === total, always.
     */
    | { mode: 'group'; faces: string[]; surplus: number; total: number }
    /** No recipient at all. */
    | { mode: 'none' };

export interface DestinationInput {
    isStatus?: boolean;
    /** Recipient ids already in hand, in send order. Empty/duplicate entries are ignored. */
    knownIds: ReadonlyArray<string | null | undefined>;
    /** How many recipients there are — may be more than `knownIds`. */
    total: number;
    /** A campaign is an audience even at one member. */
    alwaysGroup?: boolean;
}

/**
 * The one form of a recipient id an avatar is ever handed, or `null` when the
 * value is not a WhatsApp id at all.
 *
 * Two ways a group ends up drawing the SAME picture for different members, both
 * closed here — before anything reaches an avatar — rather than in the painter:
 *
 *  1. One person written two ways. Audience cells hold whatever was imported:
 *     `972501234567`, `972501234567@c.us`, `+972 50-123-4567`. An exact-string
 *     dedupe keeps all three, and the same face is drawn three times. They are
 *     all `972501234567@c.us` here.
 *
 *  2. A cell that is not an id ("Dana", "null", a group name). Handed to the
 *     engine, it does not fail: the Baileys pod's `normalizeJid` strips every
 *     non-digit and appends the server, so ANY such value becomes the same
 *     empty jid `@s.whatsapp.net`, and every one of them gets whatever single
 *     picture WhatsApp answers for it. A value that is not an id is therefore a
 *     member we cannot show — a placeholder — never a face.
 */
export function canonicalRecipientId(raw: unknown): string | null {
    if (typeof raw !== 'string') return null;
    const value = raw.trim();
    if (!value) return null;
    const at = value.indexOf('@');
    if (at === -1) {
        // A bare phone number, however it was typed.
        if (!/^[\d\s()+.-]+$/.test(value)) return null;
        const digits = value.replace(/\D/g, '');
        return digits.length >= 7 && digits.length <= 15 ? `${digits}@c.us` : null;
    }
    const user = value.slice(0, at);
    const server = value.slice(at + 1);
    switch (server) {
        case 'c.us':
        case 's.whatsapp.net':
            return /^\d{7,15}$/.test(user) ? `${user}@c.us` : null;
        case 'g.us':
            return /^\d+(-\d+)?$/.test(user) ? value : null;
        case 'lid':
        case 'newsletter':
            return /^\d+$/.test(user) ? value : null;
        default:
            return null;
    }
}

function cleanIds(ids: ReadonlyArray<string | null | undefined>): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of ids) {
        const id = canonicalRecipientId(raw);
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push(id);
    }
    return out;
}

const countOf = (n: number): number => (Number.isFinite(n) && n > 0 ? Math.floor(n) : 0);

export function planDestination(input: DestinationInput): DestinationPlan {
    if (input.isStatus) return { mode: 'status' };

    const ids = cleanIds(input.knownIds);
    // A count that is smaller than the ids we can see is stale; the ids win.
    const total = Math.max(countOf(input.total), ids.length);
    if (total === 0) return { mode: 'none' };

    if (total === 1 && !input.alwaysGroup) {
        return ids[0] ? { mode: 'single', contactId: ids[0] } : { mode: 'none' };
    }

    const faceCount = Math.min(total, MAX_FACES);
    const faces = ids.slice(0, faceCount);
    while (faces.length < faceCount) faces.push('');
    return { mode: 'group', faces, surplus: total - faceCount, total };
}

// ── from the documents ─────────────────────────────────────────────────────

interface MessageLike {
    contactList?: ReadonlyArray<{ id?: string | null } | null | undefined> | null;
    contactWAId?: string | null;
}

/** A scheduled user-message. A Status is recognised by the recipient id the
 *  send thunk keys on (`isScheduledStatus`) and is never handed to the avatar
 *  as a contact — there is no picture to resolve for `status@broadcast`. */
export function messageDestination(message: MessageLike | null | undefined): DestinationPlan {
    if (!message) return { mode: 'none' };
    if (isScheduledStatus(message as Parameters<typeof isScheduledStatus>[0])) return { mode: 'status' };
    const list = (message.contactList ?? []).map(c => c?.id);
    const knownIds = list.length ? list : [message.contactWAId];
    return planDestination({ knownIds, total: cleanIds(knownIds).length });
}

interface AudienceRow {
    boardId?: string | null;
    /** The row's name — what the audience table shows for the person. */
    text?: string | null;
    deleted?: boolean | null;
    archived?: boolean | null;
    customData?: Record<string, { value?: unknown } | null | undefined> | null;
}

/**
 * The first MAX_FACES WhatsApp ids of each audience, read from rows ALREADY in
 * the store. `waColumns` maps an audience board to its WhatsApp-id column(s) —
 * the same `whatsapp-id-cell` column the audience table renders its avatars
 * from, whose raw value it hands to ContactAvatar as-is.
 */
export function audiencePreviewIds(
    rows: ReadonlyArray<AudienceRow | null | undefined>,
    waColumns: ReadonlyMap<string, ReadonlySet<string>>,
): Record<string, string[]> {
    const out: Record<string, string[]> = {};
    const seen: Record<string, Set<string>> = {};
    for (const row of rows) {
        const boardId = row?.boardId ? String(row.boardId) : '';
        const cols = boardId ? waColumns.get(boardId) : undefined;
        if (!cols || row?.deleted || row?.archived || !row?.customData) continue;
        const list = out[boardId] ?? [];
        if (list.length >= MAX_FACES) continue;
        const had = seen[boardId] ?? (seen[boardId] = new Set());
        for (const colId of cols) {
            const value = String(row.customData[colId]?.value ?? '').trim();
            if (!value || had.has(value)) continue;
            had.add(value);
            list.push(value);
            break;
        }
        if (list.length) out[boardId] = list;
    }
    return out;
}

interface CampaignLike {
    boardIDArray?: ReadonlyArray<string | number | null | undefined> | null;
    contactCount?: number | null;
    list?: { contactIdList?: ReadonlyArray<unknown> | null } | null;
}

/**
 * How many people a campaign goes to — the ONE count the sidebar row's "N
 * contacts", its "+N" chip and the hover card all use: the document's own
 * `contactCount`, else a legacy list's length, else the audiences' row counts.
 */
export function campaignRecipientCount(
    campaign: CampaignLike | null | undefined,
    rowCountByBoard: Readonly<Record<string, number | undefined>>,
): number {
    if (!campaign) return 0;
    return campaign.contactCount
        ?? campaign.list?.contactIdList?.length
        ?? ((campaign.boardIDArray ?? []).reduce<number>((sum, id) => sum + (rowCountByBoard[String(id ?? '')] ?? 0), 0) || 0);
}

/**
 * A campaign: always a group. Faces come from its audiences in audience order;
 * the count comes from the document (`contactCount`), else from `fallbackCount`
 * — the caller's existing count, so the "+N" and the row's "N contacts" can
 * never disagree.
 */
export function campaignDestination(
    campaign: CampaignLike | null | undefined,
    previewByBoard: Readonly<Record<string, ReadonlyArray<string>>>,
    fallbackCount = 0,
): DestinationPlan {
    if (!campaign) return { mode: 'none' };
    const knownIds = (campaign.boardIDArray ?? []).flatMap(id => previewByBoard[String(id ?? '')] ?? []);
    const total = typeof campaign.contactCount === 'number' ? campaign.contactCount : fallbackCount;
    return planDestination({ knownIds, total, alwaysGroup: true });
}

/**
 * What the group's ONE accessible name is built from: the whole count and up to
 * three names, in face order. A face with no name is skipped rather than named
 * blank; a placeholder is never asked. The sentence itself is the caller's, from
 * the locale — this returns parts, not grammar.
 */
export function destinationLabelParts(
    plan: DestinationPlan,
    nameOf: (contactId: string) => string | undefined,
): { count: number; names: string[] } | null {
    if (plan.mode !== 'group') return null;
    const names: string[] = [];
    for (const id of plan.faces) {
        if (names.length >= 3) break;
        if (!id) continue;
        const name = String(nameOf(id) ?? '').trim();
        if (name) names.push(name);
    }
    return { count: plan.total, names };
}

// ── The row's leading slot ──────────────────────────────────────────────────

/**
 * What the leading slot of a row draws: ONE face, and how many others.
 *
 * The slot has a single footprint for every row — one person, a campaign of two
 * thousand, a Status — so the titles beside it line up down the whole list. A
 * stack of five faces made a campaign row 84px wide against a person's 38px and
 * pushed its text across; the owner asked for one face and "+N" instead.
 *
 * `face` is the first member we can actually show ('' draws the placeholder);
 * `more` is everyone else, so face + more is always the whole audience.
 */
export interface SlotPlan {
    face: string;
    more: number;
    isStatus: boolean;
}

export function planSlot(plan: DestinationPlan): SlotPlan | null {
    switch (plan.mode) {
        case 'none': return null;
        case 'status': return { face: '', more: 0, isStatus: true };
        case 'single': return { face: plan.contactId, more: 0, isStatus: false };
        case 'group': return { face: plan.faces.find(Boolean) ?? '', more: Math.max(plan.total - 1, 0), isStatus: false };
    }
}

// ── Who is in it: the hover list ────────────────────────────────────────────

/**
 * How many people the hover list names before it says "and N more". Five is
 * also exactly how many ids an audience preview carries (MAX_FACES), so the
 * list never asks for a member nobody has loaded.
 */
export const HOVER_LIST_MAX = MAX_FACES;

export interface MemberList {
    /** Real ids, in send order — never a placeholder. */
    shown: string[];
    /** Everyone not listed, known or not. */
    rest: number;
    total: number;
}

export function planMemberList(plan: DestinationPlan): MemberList | null {
    if (plan.mode === 'single') return { shown: [plan.contactId], rest: 0, total: 1 };
    if (plan.mode !== 'group') return null;
    const shown = plan.faces.filter(Boolean).slice(0, HOVER_LIST_MAX);
    return { shown, rest: plan.total - shown.length, total: plan.total };
}

/**
 * The number in the "+N" circle. Exact up to 999; past that shortened in the
 * reader's own notation and rounded DOWN, so the circle never claims more
 * people than there are ("+1.9K" for 1,999, never "+2K").
 */
export function formatMore(n: number, language: string): string {
    if (!Number.isFinite(n) || n <= 0) return '';
    const count = Math.floor(n);
    if (count < 1000) return String(count);
    const options: Intl.NumberFormatOptions & { roundingMode?: string } = {
        notation: 'compact', maximumSignificantDigits: 2, roundingMode: 'floor',
    };
    try {
        return new Intl.NumberFormat(language, options as Intl.NumberFormatOptions).format(count);
    } catch {
        return new Intl.NumberFormat('en', options as Intl.NumberFormatOptions).format(count);
    }
}

/**
 * Member names from the same audience rows the faces come from: the row's own
 * `text`, keyed by the CANONICAL id so it lands on the face it belongs to. Read
 * through the same batched preview request (one more selected field), so the
 * member list has names without a request of its own.
 */
export function audienceMemberNames(
    rows: ReadonlyArray<AudienceRow | null | undefined>,
    waColumns: ReadonlyMap<string, ReadonlySet<string>>,
): Record<string, string> {
    const out: Record<string, string> = {};
    for (const row of rows) {
        const boardId = row?.boardId ? String(row.boardId) : '';
        const cols = boardId ? waColumns.get(boardId) : undefined;
        if (!cols || row?.deleted || row?.archived || !row?.customData) continue;
        const name = String(row.text ?? '').trim();
        if (!name) continue;
        for (const colId of cols) {
            const id = canonicalRecipientId(row.customData[colId]?.value);
            if (id && !(id in out)) { out[id] = name; break; }
        }
    }
    return out;
}
